import type {
  Filters,
  PerformanceReportAnalytics,
  SourceType,
  ViewTab,
} from "@partners/ui";

import { useSegmentAnalytics } from "./useSegmentAnalytics";

const VIEW_BY_LABELS: Record<ViewTab, string> = {
  source: "Source",
  country: "Country",
  rebate: "Rebate program",
  campaign: "Campaign",
};

// Sheet order for the Apply composite: source + landing page + banner group +
// rebate program + country + campaign (PART-4116 row 80, PART-4224 facet).
const buildApplyLinkText = (selection: Filters) =>
  [
    selection.sourceType,
    selection.landingPage,
    selection.bannerGroup,
    selection.rebateProgram,
    selection.country,
    selection.campaign,
  ]
    .filter((values) => values.length > 0)
    .map((values) => values.join(","))
    .join(" + ");

export const usePerformanceReportPageAnalytics =
  (): Required<PerformanceReportAnalytics> => {
    const { trackButtonClickEvent, trackOptionClickEvent } =
      useSegmentAnalytics();

    const trackFilterButtonEvent = () =>
      trackButtonClickEvent({
        label: "Performance - Filter button",
        location: "Performance",
        extraProperties: {
          event_type: "Click",
          link_text: "Performance - Filter button",
        },
      });

    const trackFilterSourceEvent = (source: SourceType) =>
      trackButtonClickEvent({
        label: `Performance - Filter - ${source === "Links" ? "links" : "banners"}`,
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Click",
          link_text: source,
        },
      });

    const trackFilterLandingPageEvent = (values: string[]) =>
      trackOptionClickEvent({
        label: "Performance - Filter - landing page",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Option clicked",
          link_text: values.join(", "),
        },
      });

    const trackFilterBannerGroupEvent = (values: string[]) =>
      trackOptionClickEvent({
        label: "Performance - Filter - banner group",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Option clicked",
          link_text: values.join(", "),
        },
      });

    const trackFilterRebateProgramEvent = (values: string[]) =>
      trackOptionClickEvent({
        label: "Performance - Filter - rebate program",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Option clicked",
          link_text: values.join(", "),
        },
      });

    const trackFilterCountryEvent = (values: string[]) =>
      trackOptionClickEvent({
        label: "Performance - Filter - Country",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Option clicked",
          link_text: values.join(", "),
        },
      });

    const trackFilterApplyEvent = (selection: Filters) =>
      trackButtonClickEvent({
        label: "Performance - Filter - Apply button",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Click",
          link_text: buildApplyLinkText(selection),
        },
      });

    const trackFilterClearEvent = () =>
      trackButtonClickEvent({
        label: "Performance - Filter - Clear button",
        location: "Performance - Filter",
        extraProperties: {
          event_type: "Click",
          link_text: "Performance - Filter - Clear button",
        },
      });

    const trackViewByEvent = (tab: ViewTab) =>
      trackButtonClickEvent({
        label: `Performance - View by - ${VIEW_BY_LABELS[tab]}`,
        location: "Performance",
        extraProperties: {
          event_type: "Click",
          link_text: `Performance - View by - ${VIEW_BY_LABELS[tab]}`,
        },
      });

    const trackColumnsEvent = () =>
      trackButtonClickEvent({
        label: "Performance - Columns",
        location: "Performance",
        extraProperties: {
          event_type: "Click",
          link_text: "Performance - Columns",
        },
      });

    const trackExportCsvEvent = () =>
      trackButtonClickEvent({
        label: "Performance - Export CSV",
        location: "Performance",
        extraProperties: {
          event_type: "Click",
          link_text: "Performance - Export CSV",
        },
      });

    return {
      trackFilterButtonEvent,
      trackFilterSourceEvent,
      trackFilterLandingPageEvent,
      trackFilterBannerGroupEvent,
      trackFilterRebateProgramEvent,
      trackFilterCountryEvent,
      trackFilterApplyEvent,
      trackFilterClearEvent,
      trackViewByEvent,
      trackColumnsEvent,
      trackExportCsvEvent,
    };
  };
